import { useCallback, useMemo, useState } from "react";
import {
  getSelectedVisualMeshIds,
  setSelectedVisualMeshIds,
  useSelectedVisualMeshIds,
} from "../../visual-mesh-multi-selection";

/**
 * Owns every multi-selection bag the scene graph cares about and produces the
 * derived "effective batch" collections that consumers (context menus, batch
 * actions, fill colors) check against.
 *
 * Conceptually each entity type has three views:
 *   - The raw `Set` of multi-selected ids (state, used for `.has()` checks
 *     during click handling so toggling a single node stays O(1)).
 *   - A frozen array view of that set (consumed by code that wants a list).
 *   - An "effective batch" array/set: the union of the multi-selection with the
 *     single primary selection from the inspector. Most batch actions operate
 *     on this so a primary-selected item without an explicit multi-pick still
 *     participates in the action.
 *
 * Visual meshes are special: they live in a sibling Zustand-ish store
 * (`visual-mesh-multi-selection`) so other surfaces (3D viewport, inspector)
 * can share the same selection. The hook surfaces that store with a stable
 * setter signature matching the rest of the multi-selected `useState` setters.
 */
export function useSceneGraphMultiSelection({
  bodies,
  selectedBodies,
  selectedMesh,
  selectedGeom,
  selectedSite,
  selectedInertial,
  selectedJoint,
}) {
  const multiSelectedMeshes = useSelectedVisualMeshIds();
  // The visual-mesh selection is owned externally; wrap its setter so callers
  // can use the same `(prev) => next` updater idiom as the local `useState`s.
  // Bail out when the value is unchanged so we don't trigger no-op renders in
  // panels subscribed to the underlying store.
  const setMultiSelectedMeshes = useCallback((nextValue) => {
    const current = getSelectedVisualMeshIds();
    const next = typeof nextValue === "function" ? nextValue(current) : nextValue;
    if (next === current) return;
    setSelectedVisualMeshIds(next);
  }, []);

  const [multiSelectedGeoms, setMultiSelectedGeoms] = useState(new Set());
  const [multiSelectedSites, setMultiSelectedSites] = useState(new Set());
  const [multiSelectedInertials, setMultiSelectedInertials] = useState(new Set());
  const [multiSelectedJoints, setMultiSelectedJoints] = useState(new Set());

  const selectedMeshIds = useMemo(() => Array.from(multiSelectedMeshes), [multiSelectedMeshes]);
  const selectedGeomKeys = useMemo(() => Array.from(multiSelectedGeoms), [multiSelectedGeoms]);
  const selectedSiteIds = useMemo(() => Array.from(multiSelectedSites), [multiSelectedSites]);
  const selectedInertialKeys = useMemo(
    () => Array.from(multiSelectedInertials),
    [multiSelectedInertials]
  );
  const selectedJointIds = useMemo(() => Array.from(multiSelectedJoints), [multiSelectedJoints]);

  // Body batch is just the primary selection list — there is no separate
  // multi-selection bag for bodies. The selectionStore already supports
  // multi-selecting bodies natively, so `selectedBodies` is itself the batch.
  const effectiveBatchBodyUuids = selectedBodies;

  const effectiveBatchMeshIds = useMemo(() => {
    const merged = new Set(selectedMeshIds);
    if (selectedMesh) merged.add(selectedMesh);
    return Array.from(merged);
  }, [selectedMeshIds, selectedMesh]);

  // When a visual mesh is multi-selected, the geoms that reference that mesh
  // are implicitly part of the geom batch — otherwise duplicate/delete on a
  // visual selection would leave the underlying geoms behind.
  const selectedVisualMeshGeomKeys = useMemo(() => {
    if (selectedMeshIds.length === 0) return [];
    const meshIdSet = new Set(selectedMeshIds.map(String));
    const keys = [];
    Object.values(bodies || {}).forEach((body) => {
      if (!body?.uuid || !Array.isArray(body.geoms)) return;
      body.geoms.forEach((geom) => {
        if (!geom?.uuid || geom.visualOnly !== true || !geom.mesh) return;
        if (meshIdSet.has(String(geom.mesh))) {
          keys.push(`${body.uuid}:${String(geom.uuid)}`);
        }
      });
    });
    return keys;
  }, [bodies, selectedMeshIds]);

  const effectiveBatchGeomKeys = useMemo(() => {
    const merged = new Set(selectedGeomKeys);
    selectedVisualMeshGeomKeys.forEach((key) => merged.add(key));
    if (selectedGeom) merged.add(selectedGeom);
    return Array.from(merged);
  }, [selectedGeomKeys, selectedVisualMeshGeomKeys, selectedGeom]);

  const effectiveBatchSiteIds = useMemo(() => {
    const merged = new Set(selectedSiteIds);
    if (selectedSite) merged.add(selectedSite);
    return Array.from(merged);
  }, [selectedSiteIds, selectedSite]);

  const effectiveBatchInertialKeys = useMemo(() => {
    const merged = new Set(selectedInertialKeys);
    if (selectedInertial) merged.add(selectedInertial);
    return Array.from(merged);
  }, [selectedInertialKeys, selectedInertial]);

  const effectiveBatchJointIds = useMemo(() => {
    const merged = new Set(selectedJointIds);
    if (selectedJoint) merged.add(selectedJoint);
    return Array.from(merged);
  }, [selectedJointIds, selectedJoint]);

  // Set wrappers for O(1) `.has()` checks during render — kept memoized so
  // downstream useMemo/useCallback dependency arrays stay stable when the
  // batch contents are unchanged.
  const effectiveBatchBodyUuidSet = useMemo(
    () => new Set(effectiveBatchBodyUuids),
    [effectiveBatchBodyUuids]
  );
  const effectiveBatchMeshIdSet = useMemo(
    () => new Set(effectiveBatchMeshIds),
    [effectiveBatchMeshIds]
  );
  const effectiveBatchGeomKeySet = useMemo(
    () => new Set(effectiveBatchGeomKeys),
    [effectiveBatchGeomKeys]
  );
  const effectiveBatchSiteIdSet = useMemo(
    () => new Set(effectiveBatchSiteIds),
    [effectiveBatchSiteIds]
  );
  const effectiveBatchInertialKeySet = useMemo(
    () => new Set(effectiveBatchInertialKeys),
    [effectiveBatchInertialKeys]
  );
  const effectiveBatchJointIdSet = useMemo(
    () => new Set(effectiveBatchJointIds),
    [effectiveBatchJointIds]
  );

  return {
    multiSelectedMeshes,
    setMultiSelectedMeshes,
    multiSelectedGeoms,
    setMultiSelectedGeoms,
    multiSelectedSites,
    setMultiSelectedSites,
    multiSelectedInertials,
    setMultiSelectedInertials,
    multiSelectedJoints,
    setMultiSelectedJoints,
    effectiveBatchBodyUuids,
    effectiveBatchMeshIds,
    effectiveBatchGeomKeys,
    effectiveBatchSiteIds,
    effectiveBatchInertialKeys,
    effectiveBatchJointIds,
    effectiveBatchBodyUuidSet,
    effectiveBatchMeshIdSet,
    effectiveBatchGeomKeySet,
    effectiveBatchSiteIdSet,
    effectiveBatchInertialKeySet,
    effectiveBatchJointIdSet,
  };
}
